/**
 * @file: ConstructionManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { CommonTaskStatus, InspectionTaskJobStatus } from '@euler/model/enum';
import {
  CachedTaskDetail,
  Construction,
  ConstructionJobContextData,
  ScheduleConstructionJobPayload,
  TaskDetailVersions,
  VersionedResponse,
} from '@euler/model/task-detail';
import { array2map } from '@euler/utils/array';
import { dateFromValue } from '@euler/utils/datetime';
import { WritableDraft } from 'immer/dist/internal';
import { TaskWorkflowNodeManager } from '../TaskWorkflowNodeManager';
import { IConstructionManager, ITaskManager } from '../types';
import { ConstructionJobManager } from './ConstructionJobManager';

export class ConstructionManager
  extends TaskWorkflowNodeManager
  implements IConstructionManager
{
  private readonly jobManagers = new Map<number, ConstructionJobManager>();

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(taskManager: ITaskManager) {
    super(taskManager);
  }

  dispose() {
    for (const manager of this.jobManagers.values()) {
      manager.dispose();
    }
    this.jobManagers.clear();
  }

  async scheduleJobs(payloads: ScheduleConstructionJobPayload[]) {
    const existingJobsByName = array2map(
      this.cachedDetail.construction.jobs,
      x => x.name,
    );
    const resp = await this.taskService.scheduleConstructionJobs(
      this.taskNo,
      payloads.map<ScheduleConstructionJobPayload>(payload => ({
        ...payload,
        context: existingJobsByName.has(payload.name)
          ? undefined
          : payload.context,
      })),
      undefined,
      undefined,
      {
        restrictFallbackUserRole: true,
      },
    );
    this.handleVersionedResponse(resp, (_, { construction, response }) => {
      construction.jobs = response;
      if (!construction.isScheduled) {
        construction.isScheduled = true;
        construction.scheduledAt = dateFromValue(resp.timestamp);
      }
    });
  }

  async addConstructionJob(params: {
    name: string;
    technicianId?: number;
    technicianName?: string;
    contextData?: ConstructionJobContextData;
  }) {
    const resp = await this.taskService.addConstructionJob(this.taskNo, {
      ...params,
      options: { restrictFallbackUserRole: true },
    });
    this.handleVersionedResponse(resp, (_, { construction, response }) => {
      const index = construction.jobs.findIndex(x => x.name === response.name);
      if (index < 0) {
        construction.jobs.push(response);
      } else {
        construction.jobs[index] = response;
      }
      if (!construction.isScheduled) {
        construction.isScheduled = true;
        construction.scheduledAt = dateFromValue(resp.timestamp);
      }
    });
  }

  async beginConstructionJob(
    id: number,
    technicianId: number,
    technicianName: string,
  ) {
    const index = this.cachedDetail.construction.jobs.findIndex(
      x => x.id === id,
    );
    if (index < 0) {
      throw new Error('Job does not exist');
    }
    const job = this.cachedDetail.construction.jobs[index];
    if (job.status === InspectionTaskJobStatus.Pending) {
      const resp = await this.taskService.beginScheduledConstructionJob(
        this.taskNo,
        id,
        technicianId,
        technicianName,
      );
      this.handleVersionedResponse(resp, (_, { construction }) => {
        construction.jobs[index] = job;
        construction.jobs[index].status = InspectionTaskJobStatus.InProgress;
        construction.jobs[index].updatedAt = dateFromValue(resp.timestamp);
        construction.jobs[index].technicianId = technicianId;
        construction.jobs[index].technicianName = technicianName;
      });
    }
  }

  getConstructionJobManager(jobId: number) {
    if (!this.jobManagers.has(jobId)) {
      const job = this.cachedDetail.construction.jobs.find(x => x.id === jobId);
      if (!job) {
        throw new Error('Construction job not found, this must be a bug');
      }
      const jobManager = new ConstructionJobManager(
        this,
        job,
        this.currentUser.memberId,
        this.currentUser.name,
      );
      this.jobManagers.set(jobId, jobManager);
    }
    return this.jobManagers.get(jobId)!;
  }

  endConstructionJob(jobId: number) {
    const jobManager = this.jobManagers.get(jobId);
    if (jobManager) {
      this.jobManagers.delete(jobId);
      jobManager.dispose();
    }
  }

  async cancelConstructionJob(
    jobId: number,
    params?: {
      technicianId?: number;
      technicianName?: string;
      confirmedDeliveryCheckIdToBeDeleted?: number;
    },
  ) {
    const resp = await this.taskService.deleteScheduledConstructionJob(
      this.taskNo,
      jobId,
      params,
    );
    this.handleVersionedResponse(resp, (draft, { construction, original }) => {
      const index = original.jobs.findIndex(x => x.id === jobId);
      if (index < 0) {
        console.warn(
          'Cannot find the construction job to be cancelled, this must be bug',
        );
        return;
      }
      construction.jobs.splice(index, 1);

      // if the associated delivery check was deleted, remove the delivery
      // check item and update the delivery check status as well if necessary.
      if (params?.confirmedDeliveryCheckIdToBeDeleted != null) {
        const k = draft.deliveryCheck.items.findIndex(
          x => x.id === params.confirmedDeliveryCheckIdToBeDeleted,
        );
        if (k >= 0) {
          draft.deliveryCheck.items.splice(k, 1);
          if (!draft.deliveryCheck.items.length) {
            draft.deliveryCheck.finishedAt = undefined;
            draft.deliveryCheck.finishedBy = undefined;
            draft.deliveryCheck.status = CommonTaskStatus.Pending;
          }
        }
      }

      if (!construction.jobs.length) {
        // reset the construction status if the jobs are emptied
        construction.status = CommonTaskStatus.Pending;
        construction.finishedAt = undefined;
        construction.finishedBy = undefined;
        construction.isScheduled = false;
        construction.scheduledAt = undefined;
      }
    });
    const jobManager = this.jobManagers.get(jobId);
    if (jobManager) {
      await jobManager.discardCache();
      jobManager.dispose();
      this.jobManagers.delete(jobId);
    }
  }

  async finish() {
    const resp = await this.taskService.finishConstructionJobs(this.taskNo);
    this.handleVersionedResponse(resp, (_, { construction }) => {
      construction.finishedAt = dateFromValue(resp.timestamp);
      construction.finishedBy = this.taskManager.currentUser.user.name;
      construction.status = CommonTaskStatus.Finished;
    });
  }

  async requestAmend() {
    const resp = await this.taskService.requestUpdateConstructionJobs(
      this.taskNo,
    );
    this.handleVersionedResponse(resp, (_, { construction }) => {
      construction.finishedAt = undefined;
      construction.finishedBy = undefined;
      construction.status = CommonTaskStatus.InProgress;
    });
  }

  handleVersionedResponse<U>(
    versionedResponse: VersionedResponse<U>,
    recipe: (
      draft: WritableDraft<CachedTaskDetail>,
      context: {
        response: U;
        construction: Construction;
        version: number;
        original: Construction;
      },
    ) => void,
  ) {
    return this.produceWithVersions(
      versionedResponse.versions,
      (draft, context) => {
        recipe(draft, { ...context, response: versionedResponse.response });
      },
    );
  }

  produceWithVersions(
    versions: TaskDetailVersions,
    recipe: (
      draft: WritableDraft<CachedTaskDetail>,
      context: {
        construction: Construction;
        version: number;
        original: Construction;
      },
    ) => void,
  ) {
    const original = this.cachedDetail.construction;
    const version = versions.construction;

    if (
      version === original.version &&
      this.cachedDetail.version === versions.detail
    ) {
      // nothing changed
      return this.cachedDetail;
    }

    return this.taskManager.produceWithVersions(versions, draft => {
      const { construction } = draft;
      recipe(draft, {
        construction,
        version,
        original,
      });
      if (construction.version !== version) {
        construction.version = version;
      }
    });
  }
}
